import React, { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TablePagination,
} from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import useMetric from '../../../lib/hooks/useMetric';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { PatientLatestAssessmentRow } from './index';
import { ArrowForward, FileDownload, SaveAlt } from '@mui/icons-material';
import { IconButton } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    marginTop: 16,
  },
  icon: theme.globalClasses.adminTableIcon,
  topBar: {
    marginBottom: 16,
  },
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
  graphContainer: {
    // Add any existing styles for the graph container
  },
  inputOverride: {
    '& .MuiSvgIcon-root': {
      fontSize: '24px !important',
    },
  },
  '@media print': {
    graphContainer: {
      overflow: 'visible',
      transform: 'scale(0.50)',
      transformOrigin: 'top left',
      width: '100%',
      height: 'auto',
      position: 'relative',
    },
    // graphContainer: {
    //   overflow: 'visible',
    //   transform: 'none',
    //   width: 'auto',
    //   height: 'auto',
    // },
  },
  link: theme.globalClasses.link,
}));

const PatientLatestAssessmentsBox = ({ summary, setSetSubtab }) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);
  const { isLoading, data } = useMetric('latest_assessments', currentTab.id);

  // Add state for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(summary ? 3 : 10);
  const [isLastPage, setIsLastPage] = useState(false);

  // Determine actual data
  const assessmentData = data?.metric?.value || [];
  const totalCount = assessmentData.length;

  // Update isLastPage and handle page constraints when data changes
  useEffect(() => {
    if (assessmentData.length > 0) {
      const maxPage = Math.ceil(assessmentData.length / rowsPerPage) - 1;

      // If current page is greater than max page, reset to max page
      if (page > maxPage) {
        setPage(maxPage);
      }

      setIsLastPage((page + 1) * rowsPerPage >= assessmentData.length);
    }
  }, [assessmentData, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    if (!isLastPage || newPage < page) {
      setPage(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate start and end row indices
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalCount);

  // Always show pagination, but with different options based on view
  const paginationRowsOptions = summary ? [3, 6, 9] : [10, 25, 50];

  const topBarMargin = summary ? '12px' : '16px';

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className="patient-latest-assessments-box"
          id="patient-latest-assessments-box"
        >
          <Grid
            container
            item
            xs={12}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: '100%',
              maxWidth: '100%',
              mx: 0,
              padding: 0,
              marginBottom: topBarMargin,
            }}
          >
            {summary ? (
              <>
                <Typography
                  variant={'overline'}
                  sx={{ paddingBottom: 0 }}
                >
                  Past Assessments
                </Typography>
                <Typography
                  variant={'tableHeadLink'}
                  onClick={() => setSetSubtab('2')}
                  sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0 }}
                >
                  View All Assessments
                  <IconButton sx={{ paddingLeft: '8px' }}>
                    <ArrowForward className={classes.icon} />
                  </IconButton>
                </Typography>
              </>
            ) : (
              <Typography variant={'h2'}>Past Assessments</Typography>
            )}
          </Grid>

          {isLoading ? (
            <AdminProgressBar />
          ) : (
            <Box className={`${classes.graphContainer}`}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="left">Date</TableCell>
                    <TableCell align="left">Diet Quality</TableCell>
                    <TableCell align="left">Trend</TableCell>
                    <TableCell align="left">Goal Diet</TableCell>
                    <TableCell align="left">HEI</TableCell>
                    <TableCell align="left">BMI</TableCell>
                    <TableCell align="left">Weight</TableCell>
                    <TableCell align="left">Days Between</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessmentData.length > 0 ? (
                    assessmentData.slice(summary ? 0 : startIndex, summary ? 3 : endIndex).map((item, index, array) => {
                      const previousReport = index < array.length - 1 ? array[index + 1] : null;
                      return (
                        <PatientLatestAssessmentRow
                          key={index}
                          row={item}
                          summary={summary}
                          previousReport={previousReport}
                        />
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                      >
                        No assessment data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {!isLoading && (
            <TablePagination
              rowsPerPageOptions={summary ? [] : [10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={summary ? 3 : rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={summary ? undefined : handleChangeRowsPerPage}
              labelRowsPerPage={summary ? '' : 'Rows per page:'}
              getItemAriaLabel={(type) => `Go to ${type} page`}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientLatestAssessmentsBox;
